import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import WestIcon from '@mui/icons-material/West';
import { Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import colors from '../../../scss/colors';
import { useWindowSize } from '../../../utils/hooks';
import { ClientStep, DateStep, HoursStep } from './components/index';
import classes from './CreateTimeandExpense.module.scss';
import { responsiveSizeTimesheet } from '../../../utils/constants';
import StepsWrapper from '../../common/wrappers/StepsWrapper/StepsWrapper';
import NavigationComponent from '../../common/NavigationComponent/NavigationComponent';
import { basicErrorHandler, buildMonth, getAllDaysInMonth, getBiMonthlyDays } from '../../../utils/helpers';
import { tneAxiosClient } from '../../../api/apiSettings';
import { getTimesheets } from '../../../actions/timesheets';
import NotificationModal from '../../common/ModalWindows/NotificationModal';
import { generalEnums, rateEnum, submissionEnum } from '../../../utils/enums';
import CurrencyStep from './components/CurrencyStep/CurrencyStep';
import ExpenseDetailStep from './components/ExpenseDetailStep/ExpenseDetailStep';
import { getPlacements } from '../../../actions/constFromApi';
import TextButton from '../../common/buttons/TextButton/TextButton';
import { getTimeExpenseValidation } from '../../../actions/timesheet';
import { getDisableRule, getValidationMessage } from './utils/config';

const CopyTextButton = ({ clickHandler, t }) => (
  <Button
    variant="text"
    style={{ cursor: 'pointer' }}
    onClick={clickHandler}
    sx={{
      textDecoration: 'underline',
      textTransform: 'none',
      marginTop: '16px',
      fontSize: '1rem',
      color: colors.NAVY_BLUE,
      '&:hover': { textDecoration: 'underline', background: 'none' }
    }}
  >
    {t('common.general.copyfromrecent')}
  </Button>
);

export default function CreateTimeandExpense({ type }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const size = useWindowSize();
  const clients = useSelector((state) => state.constFromApi.clients);
  const currencies = useSelector((state) => state.constFromApi.currencies);
  const allowedWorkTypes = useSelector((state) => state.constFromApi.worktypes);
  const timesheetsList = useSelector((state) => state.timesheets.data);
  const titles = useSelector((state) => state.constFromApi.placements);
  const expensetypes = useSelector((state) => state.constFromApi.expensetypes);
  const timesheetValidationStatus = useSelector((state) => state?.timesheet?.validate?.timesheet);
  const expenseValidationStatus = useSelector((state) => state?.timesheet?.validate?.expense);
  const validationPending = useSelector((state) => state?.timesheet?.validatePending);

  const [claimDescription, setClaimDescription] = useState('');
  const [description, setDescription] = useState('');
  const [fxRate, setFxRate] = useState(null);
  const [rateType, setRateType] = useState('');
  const [timesheetType, setTimesheetType] = useState('');
  const [userChoosedMonth, setUserChoosedMonth] = useState('');
  const [validationMsg, setValidationMsg] = useState('');
  const [amount, setAmount] = useState(null);
  const [currencyValue, setCurrency] = useState(null);
  const [category, setCategory] = useState(null);
  const [claimDate, setClaimDate] = useState(dayjs());
  const [isNoReceiptChecked, setNoReceiptChecked] = useState(false);
  const [expenseData, setExpenseData] = useState({
    CategoryId: '',
    Date: '',
    Comment: '',
    ExpenseAmount: '',
    ExpenseCurrency: '',
    FxAmount: '',
    FxCurrency: '',
    attachments: [],
    UserId: parseInt(localStorage.getItem('UserId'), 10),
    PoNumber: '',
    ClientName: '',
    JobTitle: '',
    ClaimDescription: '',
    PayCurrency: ''
  });

  const initData = {
    UserId: localStorage.getItem('UserId'),
    ClientName: '',
    PlacementId: '',
    PoNumber: '',
    StartDate: '',
    TimesheetDetails: [
      {
        Location: '',
        WorkTypeId: '',
        Comment: '',
        TimesheetHoursByDates: []
      }
    ],
    TimesheetPerDiem: [],
    IsSplitApprovalFlow: false
  };

  const [data, setData] = useState(initData);

  const [currentStep, setCurrentStep] = useState(0);
  const [disabled, setDisabled] = useState(true);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const setClient = (value) => {
    setValidationMsg('');

    if (copied) {
      setCopied(false);
      setRateType('');
      setTimesheetType('');
      setUserChoosedMonth('');
      setData({
        ...initData,
        ClientName: value.label
      });
      return;
    }

    setData(() => ({
      ...initData,
      ClientName: value.label
    }));
    setExpenseData((prev) => ({
      ...prev,
      ClientName: value.label,
      JobTitle: '',
      PoNumber: ''
    }));
  };

  const setTitle = (value) => {
    const payCurrency = titles?.find((item) => item.Id === value.Id)?.PayCurrency;
    const isSplit = value.IsSplitApprovalFlow || false;

    if (copied) {
      setCopied(false);
      setUserChoosedMonth('');
    }

    setCurrency(payCurrency);
    setValidationMsg('');
    setRateType(value.RateType.toLowerCase());
    setTimesheetType(value.Frequency.toLowerCase());
    setData((prev) => ({
      ...prev,
      PlacementId: value.Id,
      PoNumber: value.PoNumber,
      StartDate: value.startDate,
      IsSplitApprovalFlow: isSplit
    }));
    setExpenseData((prev) => ({
      ...prev,
      PlacementId: value.Id,
      PoNumber: value.PoNumber,
      JobTitle: value.JobTitle,
      PayCurrency: payCurrency,
      ExpenseCurrency: payCurrency,
      FxCurrency: payCurrency,
      Date: dayjs().format(generalEnums.DATEFORMAT)
    }));
  };

  const setDate = (date) => {
    let startDate = '';
    if (date.getDay() === 0) {
      startDate = dayjs(date.toString()).add(-7, 'day').day(2).toString();
    } else {
      startDate = dayjs(date.toString()).toString();
    }
    setUserChoosedMonth(date.getMonth());
    if (copied) {
      setData((prev) => {
        if (timesheetType === submissionEnum.WEEKLY) {
          return {
            ...prev,
            TimesheetDetails: prev.TimesheetDetails.map((ts) => ({
              ...ts,
              TimesheetHoursByDates: Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
                TotalHrs: '0',
                Day: dayjs(startDate).add(increaser, 'day').format('YYYY-MM-DD')
              }))
            })),
            TimesheetPerDiem: Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
              IsPerDiem: false,
              Date: dayjs(startDate).add(increaser, 'day').format('YYYY-MM-DD')
            }))
          };
        }
        // TODO - check with Firnas for 34 days
        // const actualDate = dayjs(date);
        // const month = buildMonth(actualDate);
        // month.shift();
        // const datesToAdd = month.flat();

        const actualDate = dayjs(date);
        const year = actualDate.year();
        const month = actualDate.month() + 1;

        let datesToAdd = [];

        switch (timesheetType) {
          case submissionEnum.MONTHLY:
            datesToAdd = getAllDaysInMonth(year, month);
            break;
          case submissionEnum.BI_WEEKLY:
            datesToAdd = getBiMonthlyDays(data.StartDate, true);
            break;
          default:
            datesToAdd = getBiMonthlyDays(actualDate);
        }

        return {
          ...prev,
          TimesheetDetails: prev.TimesheetDetails.map((ts) => ({
            ...ts,
            TimesheetHoursByDates: [
              // TODO - Check with Firnas
              // ...Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
              //   TotalHrs: '0',
              //   Day: dayjs(startDate)
              //     .add(increaser - 1, 'day')
              //     .format('YYYY-MM-DD')
              // })),
              ...datesToAdd.map((day) => ({
                TotalHrs: '0',
                Day: dayjs(day).format('YYYY-MM-DD')
              }))
            ]
          })),
          TimesheetPerDiem: [
            ...datesToAdd.map((day) => ({
              IsPerDiem: false,
              Date: dayjs(day).format('YYYY-MM-DD')
            }))
          ]
        };
      });

      return;
    }
    setData((prev) => {
      if (timesheetType === submissionEnum.WEEKLY) {
        return {
          ...prev,
          TimesheetDetails: [
            {
              ...prev.TimesheetDetails[0],
              TimesheetHoursByDates: Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
                TotalHrs: '0',
                Day: dayjs(startDate).add(increaser, 'day').format(generalEnums.DATEFORMAT)
              }))
            }
          ],
          TimesheetPerDiem: Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
            IsPerDiem: false,
            Date: dayjs(startDate).add(increaser, 'day').format('YYYY-MM-DD')
          }))
        };
      }

      const actualDate = dayjs(date);
      const year = actualDate.year();
      const month = actualDate.month() + 1;

      let datesToAdd = [];

      switch (timesheetType) {
        case submissionEnum.MONTHLY:
          datesToAdd = getAllDaysInMonth(year, month);
          break;
        case submissionEnum.BI_WEEKLY:
          datesToAdd = getBiMonthlyDays(actualDate, true);
          break;
        default:
          datesToAdd = getBiMonthlyDays(actualDate);
      }

      return {
        ...prev,
        TimesheetDetails: prev.TimesheetDetails.map((ts) => ({
          ...ts,
          WorkTypeId: ts.WorkTypeId || '1',
          TimesheetHoursByDates: [
            // TODO - Check with Firnas, what reason we have included this 7-days count
            // ...Array.from({ length: 7 }, (v, k) => k).map((increaser) => ({
            //   TotalHrs: '0',
            //   Day: dayjs(startDate)
            //     .add(increaser, 'day')
            //     .format('YYYY-MM-DD')
            // })),
            ...datesToAdd.map((day) => ({
              TotalHrs: '0',
              Day: day
            }))
          ]
        })),
        TimesheetPerDiem: [
          ...datesToAdd.map((day) => ({
            IsPerDiem: false,
            Date: dayjs(day).format('YYYY-MM-DD')
          }))
        ]
      };
    });
  };

  const setTimesheet = (newTimesheet) => {
    setData((prev) => ({
      ...prev,
      TimesheetDetails: [newTimesheet]
    }));
  };

  const setClaimDescriptionField = (desc) => {
    setValidationMsg('');
    setClaimDescription(desc);
    setExpenseData((prev) => ({
      ...prev,
      ClaimDescription: desc
    }));
  };

  const setAmountField = (amnt) => {
    setValidationMsg('');
    setAmount(amnt);
    setExpenseData((prev) => ({
      ...prev,
      ExpenseAmount: amnt,
      ExpenseCurrency: currencyValue,
      FxCurrency: currencyValue
    }));
  };

  const setFxRateField = (rate) => {
    setFxRate(rate);
    setExpenseData((prev) => ({
      ...prev,
      FxAmount: rate,
      FxCurrency: currencyValue
    }));
  };

  const setCurrencyField = (currency) => {
    setCurrency(currency.label);
    setExpenseData((prev) => ({
      ...prev,
      ExpenseCurrency: expenseData?.PayCurrency,
      FxCurrencyId: currency?.key
    }));
  };

  const setCategoryField = (cat) => {
    setCategory(cat);
    setExpenseData((prev) => ({
      ...prev,
      CategoryId: cat.value
    }));
  };
  const setClaimDateField = (claimDt) => {
    setClaimDate(claimDt);
    setExpenseData((prev) => ({
      ...prev,
      Date: dayjs(claimDt).format(generalEnums.DATEFORMAT)
    }));
  };
  const setDescriptionField = (desc) => {
    setValidationMsg('');
    setDescription(desc);
    setExpenseData((prev) => ({
      ...prev,
      Comment: desc
    }));
  };

  const setAttachments = ({ file }) => {
    setExpenseData((prev) => ({
      ...prev,
      attachments: file
    }));
  };

  const handleSetNoReceiptCheck = (isChecked) => {
    if (isChecked) {
      setValidationMsg('');
      setNoReceiptChecked(true);
      setExpenseData((prev) => ({
        ...prev,
        attachments: []
      }));
    } else {
      setNoReceiptChecked(false);
    }
  };

  const setPerDiem = (date, value) => {
    setData((prev) => ({
      ...prev,
      TimesheetPerDiem: prev.TimesheetPerDiem.map((perdiem) => {
        return dayjs(perdiem.Date).isSame(date) ? { ...perdiem, IsPerDiem: value } : perdiem;
      })
    }));
  };

  const onSubmit = () => {
    setSaving(true);
    setTimeout(async () => {
      if (
        (type === generalEnums.TIMESHEET &&
          timesheetType === submissionEnum.WEEKLY &&
          data.TimesheetDetails[0].WorkTypeId &&
          data.TimesheetDetails[0].Location) ||
        (type === generalEnums.TIMESHEET && timesheetType === submissionEnum.MONTHLY) ||
        (type === generalEnums.TIMESHEET && timesheetType === submissionEnum.BI_MONTHLY) ||
        (type === generalEnums.TIMESHEET && timesheetType === submissionEnum.BI_WEEKLY)
      ) {
        try {
          const payload = { ...data };
          const isSplitApprovalFlow = payload.IsSplitApprovalFlow || false;

          if (isSplitApprovalFlow) {
            payload.TimesheetSections = [
              {
                TimesheetDetails: payload.TimesheetDetails,
                TimesheetPerDiem: payload.TimesheetPerDiem
              }
            ];
            // Remove flat arrays to keep payload clean if API is strict
            delete payload.TimesheetDetails;
            delete payload.TimesheetPerDiem;
            delete payload.IsSplitApprovalFlow;
            delete payload.StartDate;
          }

          if (copied) {
            const response = await tneAxiosClient.post(isSplitApprovalFlow ? '/v1/timesheet/split-approval' : '/v1/timesheet', payload);
            navigate(`/dashboard/timesheet/${response.data.Data.Id}?editMode=true&flag=created`);
            return;
          }

          const response = await tneAxiosClient.post(isSplitApprovalFlow ? '/v1/timesheet/split-approval' : '/v1/timesheet', payload);
          navigate(`/dashboard/timesheet/${response.data.Data.Id}?editMode=true&hasChangesMode=true&flag=created`);
        } catch (e) {
          // basicErrorHandler(dispatch, e.response.data);
          console.log('Error while creating timesheet:', e);
        }
      } else if (type === generalEnums.EXPENSE) {
        try {
          const resultData = { ...expenseData };

          const formData = new FormData();

          const expenseCurrencyId = currencies.find((curr) => curr?.label === expenseData?.PayCurrency)?.key;

          Object.entries(resultData).forEach(([key, value]) => {
            if (key !== 'attachments') {
              formData.append(key, value ?? '');
            }
          });

          formData.append('ExpenseCurrencyId', expenseCurrencyId);
          formData.append('ExpenseCurrency', expenseData?.ExpenseCurrency?.CurrencyName ?? '');
          formData.append('FxCurrency', expenseData?.FxCurrency?.CurrencyName ?? '');

          [...resultData.attachments].forEach((item) => {
            formData.append('attachments', item);
          });

          const response = await tneAxiosClient.post('/v1/expense', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          if (response) navigate(`/dashboard/expense/${response?.data?.Data?.Id}?editMode=true`);
        } catch (err) {
          basicErrorHandler(dispatch, err.response.data);
        }
      }
      setSaving(false);
    }, 0);
  };

  const disabledHandler = () => {
    setTimeout(() => {
      const validationMessage = getValidationMessage(t, currentStep, type, data, expenseData, isNoReceiptChecked, size);
      setValidationMsg(validationMessage);
    }, 100);
  };

  const getTitlesList = (id) => {
    dispatch(getPlacements(id));
  };

  const copyTimeSheet = async () => {
    const source = timesheetsList[0];
    let extractedDetails = [];
    let extractedPerDiem = [];

    if (source.IsSplitApprovalFlow && source.TimesheetSections?.length > 0) {
      extractedDetails = source.TimesheetSections.flatMap(s => s.TimesheetDetails);
      extractedPerDiem = source.TimesheetSections.flatMap(s => s.TimesheetPerDiem);
    } else {
      extractedDetails = source.TimesheetDetails;
      extractedPerDiem = source.TimesheetPerDiem;
    }
    setData((prev) => ({
      ...prev,
      ClientName: timesheetsList[0].ClientName,
      PoNumber: timesheetsList[0].PO,
      PlacementId: timesheetsList[0].PlacementId,
      TimesheetDetails: timesheetsList[0].TimesheetDetails.map((ts) => ({
        ...ts,
        TimesheetHoursByDates: ts.TimesheetHoursByDates.map((day) => ({ ...day, TotalHrs: 0 })),
        WorkTypeId: ts.WorkType.Id
      })),
      TimesheetPerDiem: extractedPerDiem.map(pd => ({ ...pd, IsPerDiem: false }))
    }));
    const tsType = timesheetsList[0].TimesheetType.includes('Semi')
      ? timesheetsList[0].TimesheetType.replace(/-?(Daily|Hourly)/, '').toLowerCase()
      : timesheetsList[0].TimesheetType.split('-')[0].toLowerCase();
    setTimesheetType(tsType);
    setUserChoosedMonth(new Date(timesheetsList[0].TimesheetDetails[0].TimesheetHoursByDates[6].Day).getMonth());
    const response = await tneAxiosClient.get(
      `v1/placement/${clients.find((client) => client.label === timesheetsList[0].ClientName).value}`
    );
    setRateType(response.data.Data.find((item) => item.Id === timesheetsList[0].PlacementId).RateType.toLowerCase());
    setCopied(
      tsType === submissionEnum.WEEKLY
        ? dayjs(timesheetsList[0].TimesheetDetails[0].TimesheetHoursByDates.at(-1).Day).add(1, 'day')
        : {
            month: new Date(timesheetsList[0].TimesheetDetails[0].TimesheetHoursByDates[6].Day).getMonth(),
            year: new Date(timesheetsList[0].TimesheetDetails[0].TimesheetHoursByDates[6].Day).getFullYear()
          }
    );
  };

  const getStateData = (states) => {
    setExpenseData((prev) => ({
      ...prev,
      ...states
    }));
    setNoReceiptChecked(states.isChecked);
  };
  const steps = {
    client: (
      <ClientStep
        type={type}
        key={data.ClientName}
        description={claimDescription}
        setDescription={setClaimDescriptionField}
        clients={clients || []}
        client={clients?.find((item) => item.label === data.ClientName)}
        setClient={setClient}
        title={titles?.find((item) => item.Id === data.PlacementId) || ''}
        titles={titles || []}
        setTitle={setTitle}
        error={validationMsg}
        wrapperContainer={classes.wrapperContainer}
        extraComponent={
          type === generalEnums.TIMESHEET &&
          currentStep === 0 &&
          size.width < responsiveSizeTimesheet &&
          timesheetsList?.length ? (
            <CopyTextButton clickHandler={copyTimeSheet} t={t} />
          ) : null
        }
      />
    ),
    date: (
      <DateStep
        key="date"
        setDate={setDate}
        type={timesheetType}
        choosedDate={timesheetType === submissionEnum.BI_WEEKLY ? dayjs(data.StartDate) : copied ? '' : data.TimesheetDetails?.[0].TimesheetHoursByDates[0]?.Day || ''}
        choosedMonth={timesheetType === submissionEnum.BI_WEEKLY ? dayjs(data.StartDate).month() - 1 : userChoosedMonth}
        wrapperContainer={classes.wrapperContainer}
        disabledBefore={copied}
      />
    ),
    hours: (
      <HoursStep
        key={data.TimesheetDetails[0]}
        timesheetType={timesheetType}
        rateType={rateType}
        timesheet={data.TimesheetDetails[0]}
        setTimesheet={setTimesheet}
        allowedWorkTypes={allowedWorkTypes || []}
        choosedMonth={userChoosedMonth}
        wrapperContainer={timesheetType !== submissionEnum.MONTHLY ? classes.wrapperContainer : ''}
        perdiems={data.TimesheetPerDiem}
        changePerDiem={setPerDiem}
      />
    ),
    currency: (
      <CurrencyStep
        key={expenseData.ClientName}
        titles={titles}
        data={expenseData}
        amount={amount}
        setAmount={setAmountField}
        currency={currencyValue}
        setCurrency={setCurrencyField}
        currencies={currencies}
        error={validationMsg}
        fxRate={fxRate}
        setFxRate={setFxRateField}
        isCategoryMiles={category?.value === 5}
      />
    ),
    expensedetail: (
      <ExpenseDetailStep
        getStates={getStateData}
        expenseCurrency={currencyValue}
        key={expenseData.ClientName}
        category={category}
        categories={expensetypes}
        setCategory={setCategoryField}
        date={claimDate}
        setDate={setClaimDateField}
        description={description}
        setDescription={setDescriptionField}
        attachmentHandler={(files) => setAttachments(files)}
        setNoReceiptCheck={(isChecked) => handleSetNoReceiptCheck(isChecked)}
        error={validationMsg}
      />
    )
  };

  const renderSteps = () => {
    if (type === generalEnums.TIMESHEET) {
      if (
        timesheetType === submissionEnum.WEEKLY ||
        (timesheetType === submissionEnum.MONTHLY && size.width < responsiveSizeTimesheet) ||
        (timesheetType === submissionEnum.BI_MONTHLY && size.width < responsiveSizeTimesheet) ||
        (timesheetType === submissionEnum.BI_WEEKLY && size.width < responsiveSizeTimesheet)
      ) {
        return [steps.client, steps.date, steps.hours];
      }
      return [steps.client, steps.date];
    }

    if (size.width > responsiveSizeTimesheet) {
      return [steps.client, steps.expensedetail, steps.currency];
    }

    return [steps.client, steps.expensedetail];
  };

  const handleNavigate = () => {
    if (type === generalEnums.TIMESHEET) {
      navigate('/dashboard/timesheets');
    } else {
      navigate('/dashboard/expenses');
    }
  };

  useEffect(() => {
    dispatch(getTimesheets(localStorage.getItem('UserId'), 1, 1, ''));
  }, []);

  useEffect(() => {
    if (data.ClientName) {
      getTitlesList(clients.find((item) => item.label === data.ClientName).value);
    }
  }, [data.ClientName]);

  useEffect(() => {
    const rules = getDisableRule(
      currentStep,
      type,
      data,
      expenseData,
      isNoReceiptChecked,
      currencyValue,
      amount,
      fxRate
    );
    setDisabled(rules);
  }, [
    data,
    expenseData,
    currentStep,
    generalEnums,
    currencyValue,
    amount,
    fxRate,
    isNoReceiptChecked,
    timesheetValidationStatus,
    expenseValidationStatus
  ]);

  const stepConfig = {
    title:
      type === generalEnums.TIMESHEET ? t('dashboard.createtimesheet.navpanel') : t('dashboard.createclaim.navpanel')
  };

  useEffect(() => {
    dispatch(getTimeExpenseValidation('', ''));
  }, []);

  useEffect(() => {
    if (data?.ClientName && data?.PlacementId && titles?.length) {
      const companyId = clients.find((item) => item.label === data.ClientName).value;
      const jobId = titles?.find((item) => item.Id === data.PlacementId)?.JobId;
      dispatch(getTimeExpenseValidation(companyId, jobId));
    }
  }, [data.ClientName, data.PlacementId, titles]);

  useEffect(() => {
    if (type === generalEnums.TIMESHEET && currentStep === 0 && !timesheetValidationStatus && !validationPending) {
      setDisabled(true);
      setValidationMsg(t('dashboard.cards.timesheetvalidate'));
      return;
    }

    if (type === generalEnums.EXPENSE && currentStep === 0 && !expenseValidationStatus && !validationPending) {
      setDisabled(true);
      setValidationMsg(t('dashboard.cards.expensevalidate'));
      return;
    }

    setValidationMsg('');
  }, [timesheetValidationStatus, expenseValidationStatus, validationPending]);

  return (
    <section className={classes.container}>
      {size.width <= responsiveSizeTimesheet && (
        <div className={classes.headerNavigationWrapper}>
          <NavigationComponent
            label={stepConfig.title}
            linkBack={
              currentStep !== 0 ? (
                <WestIcon onClick={() => setCurrentStep((prev) => prev - 1)} sx={{ color: '#003B58' }} />
              ) : (
                <div />
              )
            }
          >
            {currentStep === 0 && <CloseIcon onClick={handleNavigate} sx={{ color: '#003B58', fontSize: '28px' }} />}
          </NavigationComponent>
        </div>
      )}
      <StepsWrapper
        steps={copied ? [steps.client, steps.date] : renderSteps()}
        disabled={disabled}
        saving={saving}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        onSubmit={onSubmit}
        onClose={() => navigate(-1)}
        additionalComponent={
          type === generalEnums.TIMESHEET &&
          currentStep === 0 &&
          size.width > responsiveSizeTimesheet &&
          timesheetsList?.length ? (
            <TextButton clickHandler={copyTimeSheet}>{t('common.general.copyfromrecent')}</TextButton>
          ) : null
        }
        disabledHandler={disabledHandler}
        width={size.width <= responsiveSizeTimesheet ? '100%' : '820px'}
        progressBarLength={size.width <= responsiveSizeTimesheet ? '100%' : '400px'}
        withBackButton={size.width > responsiveSizeTimesheet}
        error={validationMsg}
      />
      {/* {validationMsg && size.width < responsiveSizeTimesheet && (
        <NotificationModal open={validationMsg} onClose={() => setValidationMsg('')}>
          <p className={classes.errorMessage}>{validationMsg}</p>
        </NotificationModal>
      )} */}
    </section>
  );
}
